import {createRouter, createWebHistory, RouteMeta, RouteRecordRaw} from 'vue-router'
import NotFoundView from "@/views/NotFoundView.vue";
import { updateMetaTags } from '@/utils/seo';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: () => import(/* webpackChunkName: "about" */ '../pages/HomePage.vue'),
    meta: {
      title: "Home | MatrixHub Company Ltd.",
      description: "Your trusted partner in delivering innovative solutions and exceptional services. We provide custom web development, mobile apps, AI-powered SaaS solutions, and technology consulting in Tanzania.",
      keywords: "MatrixHub, technology solutions, web development, mobile apps, SaaS, AI solutions, software development, Tanzania, Dodoma",
      isAuthenticated: false,
    },
  },
  {
    path: '/about',
    name: 'about',
    component: () => import(/* webpackChunkName: "about" */ '../pages/AboutPage.vue'),
    meta: {
      title: "About Us | MatrixHub Company Ltd.",
      description: "Learn about MatrixHub Company Ltd. Our mission, vision and values. A technology company turning bold ideas into sustainable digital solutions in Tanzania.",
      keywords: "about MatrixHub, MatrixHub mission, MatrixHub vision, technology company Tanzania, software company Dodoma",
      isAuthenticated: false,
    },
  },
  {
    path: '/services',
    name: 'services',
    component: () => import(/* webpackChunkName: "services" */ '../pages/ServicesPage.vue'),
    meta: {
      title: "Our Services | MatrixHub Company Ltd.",
      description: "Explore MatrixHub's services. Custom web development, mobile apps, UI/UX design, cybersecurity, cloud computing, e-commerce, AI and more.",
      keywords: "web development, mobile apps, UI/UX design, cybersecurity, cloud computing, DevOps, e-commerce, AI, software services Tanzania",
      isAuthenticated: false,
    },
  },
  {
    path: '/products',
    name: 'products',
    component: () => import(/* webpackChunkName: "products" */ '../pages/ProductsPage.vue'),
    meta: {
      title: "Our Products | MatrixHub Company Ltd.",
      description: "Discover MatrixHub's products. EduCore school management system, MediCore EMR, Senda bulk SMS gateway, NiPrintie and Nipeleke delivery platform.",
      keywords: "EduCore, MediCore EMR, Senda, NiPrintie, Nipeleke, SaaS, school management system, EMR system, bulk SMS",
      isAuthenticated: false,
    },
  },
  {
    path: '/products/:slug',
    name: 'product-detail',
    component: () => import(/* webpackChunkName: "products" */ '../pages/ProductDetailPage.vue'),
    meta: {
      title: "Our Products | MatrixHub Company Ltd.",
      description: "Discover MatrixHub's products. EduCore school management system, MediCore EMR, Senda bulk SMS gateway, NiPrintie and Nipeleke.",
      keywords: "EduCore, MediCore EMR, Senda, NiPrintie, Nipeleke, SaaS, school management system, EMR system, bulk SMS",
      isAuthenticated: false,
    },
  },
  {
    path: '/who-we-serve',
    name: 'who-we-serve',
    component: () => import(/* webpackChunkName: "who-we-serve" */ '../pages/WhoWeServePage.vue'),
    meta: {
      title: "Who We Serve | MatrixHub Company Ltd.",
      description: "MatrixHub serves schools, healthcare facilities, SMEs, startups, government, finance, law, energy, agriculture, tourism, insurance and more.",
      keywords: "industries we serve, education technology, healthcare software, government solutions, SME technology, fintech solutions",
      isAuthenticated: false,
    },
  },
  {
    path: '/how-we-work',
    name: 'how-we-work',
    component: () => import(/* webpackChunkName: "how-we-work" */ '../pages/HowWeWorkPage.vue'),
    meta: {
      title: "How We Work | MatrixHub Company Ltd.",
      description: "Learn how MatrixHub works. From project planning and development to testing, launch and ongoing support.",
      keywords: "how we work, software development process, project planning, agile development, DevOps",
      isAuthenticated: false,
    },
  },
  {
    path: '/team',
    name: 'team',
    component: () => import(/* webpackChunkName: "team" */ '../pages/TeamPage.vue'),
    meta: {
      title: "Our Team | MatrixHub Company Ltd.",
      description: "Meet the MatrixHub team. The experts driving innovation, excellence and your success.",
      keywords: "MatrixHub team, leadership, software developers, designers, CEO",
      isAuthenticated: false,
    },
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import(/* webpackChunkName: "contact" */ '../pages/ContactPage.vue'),
    meta: {
      title: "Contact Us | MatrixHub Company Ltd.",
      description: "Get in touch with MatrixHub Company Ltd. in Dodoma, Tanzania. Call, email or visit us for inquiries, feedback and support.",
      keywords: "contact MatrixHub, MatrixHub Dodoma, MatrixHub Tanzania, software company contact",
      isAuthenticated: false,
    },
  },
  {
    path: '/careers',
    name: 'careers',
    component: () => import(/* webpackChunkName: "careers" */ '../pages/CareersPage.vue'),
    meta: {
      title: "Careers | MatrixHub Company Ltd.",
      description: "Join the MatrixHub team. Explore open positions and build your career with a company creating real impact in technology.",
      keywords: "MatrixHub careers, jobs at MatrixHub, software developer jobs Tanzania, join tech team",
      isAuthenticated: false,
    },
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import(/* webpackChunkName: "faq" */ '../pages/FaqPage.vue'),
    meta: {
      title: "FAQ | MatrixHub Company Ltd.",
      description: "Frequently asked questions about MatrixHub's services, pricing, timelines, support and products.",
      keywords: "MatrixHub FAQ, frequently asked questions, pricing, support, software services",
      isAuthenticated: false,
    },
  },
  {
    path: '/terms-of-service',
    name: 'terms-of-service',
    component: () => import(/* webpackChunkName: "about" */ '../views/TermsOfServiceView.vue'),
    meta: {
      title: "Terms of Service | MatrixHub",
      description: "Read MatrixHub Company Ltd.'s Terms of Service. Understand the terms and conditions for using our services and products.",
      keywords: "terms of service, MatrixHub terms, legal, conditions, service agreement",
      isAuthenticated: false,
    },
  },
  {
    path: '/privacy-policy-page',
    name: 'privacy-policy-page',
    component: () => import(/* webpackChunkName: "privacy-policy" */ '../views/PrivacyPolicyView.vue'),
    meta: {
      title: "Privacy Policy | MatrixHub",
      description: "MatrixHub's Privacy Policy. Learn how we collect, use, and protect your personal information when you use our services and products.",
      keywords: "privacy policy, data protection, MatrixHub privacy, GDPR, data security",
      isAuthenticated: false,
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: {
      title: "Page Not Found | MatrixHub",
      description: "The page you are looking for could not be found.",
      isAuthenticated: false,
    }
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    }
    return { top: 0 };
  },
});

router.beforeEach((toRoute, fromRoute, next) => {
  // Update meta tags for the route
  const meta = toRoute.meta as RouteMeta;
  if (meta) {
    updateMetaTags({
      title: meta.title || 'MatrixHub Company Ltd.',
      description: meta.description || 'Your trusted partner in delivering innovative solutions and exceptional services.',
      keywords: meta.keywords || 'MatrixHub, technology solutions, software development',
      url: window.location.origin + toRoute.path,
    });
  }
  
  if (toRoute.meta.isAuthenticated === true) {
    next("/");
  } else {
    next();
  }
});

export default router
